import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, Check, X as XIcon, Beaker, ArrowRight } from "lucide-react";
import { fetchProtocols } from "@/lib/api";
import IngredientChips from "@/components/IngredientChips";
import { useFlyerSelection, FlyerBar } from "@/components/FlyerSelection";
import { FilePlus2, FileCheck2 } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export function focusFromSearch() {
  const raw = new URLSearchParams(window.location.search).get("focus") || "";
  return raw.split(",").map((s) => decodeURIComponent(s).trim()).filter(Boolean);
}

// Customer-facing Protocol Builder. Concern chips + ranked product cards come
// straight from the Airtable Protocol table via /postcue/protocols.
export default function ProtocolBuilder({ dealerSlug, pricingByProduct = {}, onOpenProduct, initialFocus = [] }) {
  const [selected, setSelected] = useState(initialFocus);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const flyer = useFlyerSelection(dealerSlug);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchProtocols(selected)
      .then((d) => active && setData(d))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    try {
      const q = selected.length ? `&focus=${selected.map(encodeURIComponent).join(",")}` : "";
      window.history.replaceState(null, "", `/d/${dealerSlug}?view=protocols${q}`);
    } catch {
      /* ignore */
    }
    return () => {
      active = false;
    };
  }, [selected, dealerSlug]);

  const toggle = (c) => setSelected((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));
  const products = data?.products || [];
  const concerns = data?.concerns || [];

  return (
    <section className="space-y-5" data-testid="protocol-builder">
      <div className="rounded-2xl bg-[#0b2545] text-white p-5 sm:p-7 relative overflow-hidden">
        <Sparkles className="absolute -right-4 -top-4 text-white/10" size={140} />
        <div className="text-[11px] uppercase tracking-[3px] text-[#d6ad52] font-semibold">Peptide Protocols</div>
        <h1 className="mt-1 text-2xl sm:text-3xl font-bold leading-tight" data-testid="protocol-heading">Build Your OreVital Protocol</h1>
        <p className="mt-2 text-sm sm:text-base text-white/80 max-w-xl">
          Select what you would like to focus on and discover the OreVital products and peptides that fit your routine.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide">Protocol Focus</h2>
          {selected.length > 0 && (
            <button type="button" onClick={() => setSelected([])} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="protocol-clear">
              <XIcon size={12} /> Clear ({selected.length})
            </button>
          )}
        </div>
        {loading && !data ? (
          <div className="py-6 text-center text-zinc-500 text-sm"><Loader2 size={14} className="animate-spin inline mr-1" />Loading protocols…</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="protocol-focus-list">
            {concerns.map((c) => {
              const on = selected.includes(c);
              const n = data?.concern_counts?.[c] || 0;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggle(c)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all active:scale-95 ${
                    on ? "bg-violet-600 border-violet-600 text-white shadow-sm" : "bg-white border-zinc-200 text-zinc-700 hover:border-violet-300 hover:bg-violet-50"
                  }`}
                  data-testid={`protocol-focus-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                >
                  {on ? <Check size={13} /> : null}
                  {c}
                  <span className={`text-[10px] ${on ? "text-white/70" : "text-zinc-400"}`}>{n}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selected.length > 0 && data?.focus_meta && (
        <div className={`grid gap-3 ${selected.length > 1 ? "sm:grid-cols-2" : ""}`} data-testid="protocol-focus-heroes">
          {selected.slice(0, 4).map((c) => (
            <FocusHero key={c} meta={data.focus_meta[c]} dealerSlug={dealerSlug} compact={selected.length > 1} />
          ))}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide">
            {selected.length ? "Your protocol matches" : "All protocol products"}
          </h2>
          <span className="text-xs text-zinc-500" data-testid="protocol-result-count">
            {loading ? "…" : `${products.length} product${products.length === 1 ? "" : "s"}`}
          </span>
        </div>
        {!loading && products.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-sm rounded-2xl border border-dashed border-zinc-200">No products match that combination yet — try fewer focus areas.</div>
        ) : (
          <ul className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 ${loading ? "opacity-60" : ""}`} data-testid="protocol-results">
            {products.map((p) => (
              <ProtocolCard key={p.id} product={p} pricing={pricingByProduct[p.id]} selected={selected} onOpen={() => onOpenProduct(p)} inFlyer={flyer.has(p.id)} onToggleFlyer={() => flyer.toggle(p.id)} />
            ))}
          </ul>
        )}
      </div>
      <FlyerBar dealer={dealerSlug} ids={flyer.ids} onClear={flyer.clear} focus={selected[0] || ""} />
    </section>
  );
}

export function ProtocolCard({ product: p, pricing, selected, onOpen, inFlyer = false, onToggleFlyer }) {
  const msrp = Number(p.price || 0);
  const rolePrice = pricing?.role_price ? Number(pricing.role_price) : null;
  const onSale = !!pricing?.on_sale && Number(pricing?.sale_price || 0) > 0;
  const finalPrice = onSale ? Number(pricing.sale_price) : rolePrice && Math.abs(rolePrice - msrp) > 0.005 ? rolePrice : msrp;
  const strike = finalPrice < msrp;
  const why = useMemo(() => buildWhy(p, selected), [p, selected]);
  const badge = p.match_total ? `${p.match_count}/${p.match_total} match` : null;

  return (
    <li className="rounded-2xl border border-zinc-200 bg-white overflow-hidden flex flex-col hover:shadow-md hover:border-violet-200 transition-all" data-testid={`protocol-card-${p.id}`}>
      <button type="button" onClick={onOpen} className="relative block aspect-[4/3] bg-zinc-50 overflow-hidden" data-testid={`protocol-card-image-${p.id}`}>
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} className="w-full h-full object-contain p-3" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300"><Beaker size={40} /></div>
        )}
        {badge && (
          <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${p.match_count === p.match_total ? "bg-emerald-600 text-white" : "bg-white/90 text-zinc-700 border border-zinc-200"}`} data-testid={`protocol-card-badge-${p.id}`}>
            {badge}
          </span>
        )}
      </button>
      <div className="p-4 flex-1 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold text-zinc-900 leading-snug">{p.name}</h3>
          <div className="text-right shrink-0">
            <div className="text-base font-bold text-zinc-900">{fmt(finalPrice)}</div>
            {strike && <div className="text-[11px] text-zinc-400 line-through">{fmt(msrp)}</div>}
          </div>
        </div>
        <div className="flex flex-wrap gap-1" data-testid={`protocol-card-concerns-${p.id}`}>
          {(p.matched?.length ? p.matched : p.concerns).slice(0, 6).map((c) => (
            <span key={c} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.matched?.includes(c) ? "bg-violet-100 text-violet-800" : "bg-zinc-100 text-zinc-600"}`}>{c}</span>
          ))}
          {!p.matched?.length && p.concerns.length > 6 && <span className="text-[10px] text-zinc-400">+{p.concerns.length - 6} more</span>}
        </div>
        {p.ingredients.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold mb-1">Peptides & main ingredients</div>
            <ul className="space-y-1.5">
              {p.ingredients.map((ing) => (
                <li key={ing.name} className="text-xs leading-snug">
                  <span className="font-semibold text-zinc-800">{ing.name}</span>
                  {ing.benefit && <span className="text-zinc-500"> — {ing.benefit}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <IngredientChips chips={p.ingredient_chips || []} productId={p.id} />
        {why && (
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3" data-testid={`protocol-card-why-${p.id}`}>
            <div className="text-[10px] uppercase tracking-wide text-amber-700 font-bold mb-0.5">Why it fits</div>
            <p className="text-xs text-zinc-700 leading-relaxed">{why}</p>
          </div>
        )}
        <div className="mt-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-[#0b2545] text-white text-xs font-bold tracking-wide hover:bg-[#123566] transition-colors"
            data-testid={`protocol-card-view-${p.id}`}
          >
            VIEW PRODUCT <ArrowRight size={14} />
          </button>
          {onToggleFlyer && (
            <button
              type="button"
              onClick={onToggleFlyer}
              title={inFlyer ? "Remove from flyer" : "Add to flyer"}
              className={`h-10 px-3 rounded-full border text-xs font-bold inline-flex items-center gap-1 transition-colors ${inFlyer ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-emerald-300 hover:bg-emerald-50"}`}
              data-testid={`protocol-card-flyer-${p.id}`}
              aria-pressed={inFlyer}
            >
              {inFlyer ? <FileCheck2 size={14} /> : <FilePlus2 size={14} />}
              <span className="hidden sm:inline">{inFlyer ? "In flyer" : "Flyer"}</span>
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function joinList(items) {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function buildWhy(p, selected) {
  const matched = p.matched?.length ? p.matched : [];
  const lead = p.ingredients[0];
  if (!matched.length && !lead) return "";
  const parts = [];
  if (matched.length) {
    parts.push(`Chosen for ${joinList(matched)}${selected.length > matched.length ? ` (${matched.length} of your ${selected.length} focus areas)` : ""}.`);
  } else if (p.concerns.length) {
    parts.push(`Part of the ${joinList(p.concerns.slice(0, 3))} protocol${p.concerns.length > 3 ? "s" : ""}.`);
  }
  if (lead) {
    const benefit = lead.benefit ? lead.benefit.replace(/\.$/, "") : "";
    parts.push(benefit ? `${lead.name} delivers ${benefit.charAt(0).toLowerCase()}${benefit.slice(1)}.` : `Powered by ${lead.name}.`);
  }
  if (p.ingredients[1]) parts.push(`Paired with ${p.ingredients.slice(1).map((i) => i.name).join(" and ")} to round out your routine.`);
  return parts.join(" ");
}


export function FocusHero({ meta, dealerSlug, compact = false }) {
  if (!meta) return null;
  return (
    <div className={`rounded-2xl overflow-hidden border border-zinc-200 bg-white flex ${compact ? "flex-row" : "flex-col sm:flex-row"}`} data-testid={`protocol-focus-hero-${meta.slug}`}>
      {meta.image_url && (
        <div className={`${compact ? "w-24 sm:w-28" : "sm:w-64"} shrink-0 bg-zinc-50`}>
          <img src={meta.image_url} alt={meta.name} className={`w-full h-full object-cover ${compact ? "" : "max-h-48 sm:max-h-none"}`} loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} />
        </div>
      )}
      <div className={`${compact ? "p-3" : "p-5"} flex-1 min-w-0`}>
        <div className="text-[10px] uppercase tracking-[2px] text-[#b88a2b] font-bold">Protocol Focus</div>
        <h3 className={`${compact ? "text-base" : "text-xl"} font-bold text-[#0b2545] leading-tight mt-0.5`}>{meta.name}</h3>
        {meta.description && <p className={`text-zinc-600 mt-1.5 leading-relaxed ${compact ? "text-xs line-clamp-3" : "text-sm"}`}>{meta.description}</p>}
        <Link to={`/protocols/${meta.slug}?d=${dealerSlug}`} className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-violet-700 hover:underline" data-testid={`protocol-focus-open-${meta.slug}`}>
          Open {meta.name} protocol page <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}
